
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { Menu, MapPin, ChevronDown, Search, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCenterData } from '@/hooks/useCenterData';
import { coursesForState, type AustralianState } from '@/data/courses/courses';

import MegaMenu, { type MegaPanel } from './MegaMenu';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCoursesExpanded, setIsCoursesExpanded] = useState(false);
  const [megaPanel, setMegaPanel] = useState<MegaPanel>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [hideVideoSection, setHideVideoSection] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const centerSlugFromPath = location.pathname.startsWith('/centres/')
    ? location.pathname.split('/centres/')[1].split('/')[0]
    : '';
  const { data: routeCenterData } = useCenterData(centerSlugFromPath);
  const {
    selectedState,
    setSelectedState,
    selectedCenter,
    setSelectedCenter,
    selectedCenterId,
    setSelectedCenterId,
    currentStateConfig
  } = useLocationContext();

  const handleVideoEnd = () => {
    setIsVideoModalOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    setIsMenuOpen(false);
    setMegaPanel(null);
  }, [location]);

  const toggleMegaPanel = (next: Exclude<MegaPanel, null>) => {
    setMegaPanel((prev) => (prev === next ? null : next));
  };

  // Keep global selection in sync with the current centre route
  useEffect(() => {
    if (!routeCenterData) return;

    const { slug, id, state_code } = routeCenterData as {
      slug: string;
      id: string; // UUID from database
      state_code: string;
    };

    // Sync state and centre only when route changes, not when user manually changes state
    setSelectedState(state_code);
    setSelectedCenter(slug);
    setSelectedCenterId(id); // Store UUID for pricing queries

    // Suppress the global location popup once we've resolved a centre from the URL
    try {
      localStorage.setItem('schoolola_location_selected', 'true');
    } catch {
      /* ignore storage errors */
    }
  }, [routeCenterData, setSelectedState, setSelectedCenter, setSelectedCenterId]);

  // When the state changes on non-centre pages, clear the selected centre.
  // When on a centre page and state changes, navigate to programs to avoid conflict.
  // The /programs hub (incl. /programs/:state) must NEVER clear the centre -
  // it intentionally shows centre pricing when centre.state matches URL state.
  const prevCentrePathRef = useRef<string | null>(null);

  useEffect(() => {
    // Only treat a state mismatch as a conflict when the user changed state
    // while already on this centre page - not when arriving on the page
    // (initial mount or navigation), where the sync effect above adopts the
    // centre's state instead.
    const centreSlug = (routeCenterData as any)?.slug ?? null;
    const enteredPage =
      prevCentrePathRef.current !== location.pathname ||
      prevCentrePathRef.current !== `${location.pathname}|${centreSlug}`;
    prevCentrePathRef.current = `${location.pathname}|${centreSlug}`;

    if (location.pathname.startsWith('/centres/') && routeCenterData) {
      const centerState = (routeCenterData as any).state_code;
      if (centerState && centerState !== selectedState && !enteredPage) {
        navigate('/programs');
        setSelectedCenter('');
        setSelectedCenterId(null);
      }
    } else if (!location.pathname.startsWith('/centres/')) {
      const isProgramsRoute =
        location.pathname === '/programs' || location.pathname.startsWith('/programs/');
      const programPagesWithPricing = [
        '/programs/foundations',
        '/programs/oc-preparation',
        '/programs/selective-school',
        '/programs/scholarship-exam-course',
      ];
      if (!isProgramsRoute && !programPagesWithPricing.includes(location.pathname)) {
        setSelectedCenter('');
        setSelectedCenterId(null);
      }
    }
  }, [selectedState, location.pathname, routeCenterData, navigate, setSelectedCenter, setSelectedCenterId]);

  // Scroll detection for hiding video section on SCOPE page
  useEffect(() => {
    if (location.pathname !== '/scope-exam') {
      setHideVideoSection(false);
      return;
    }

    const handleScroll = () => {
      const scopeSection = document.querySelector('[data-scope-cta]');
      if (scopeSection) {
        const rect = scopeSection.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;
        setHideVideoSection(isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const states = [{
    code: 'NSW',
    name: 'New South Wales'
  }, {
    code: 'VIC',
    name: 'Victoria'
  }, {
    code: 'QLD',
    name: 'Queensland'
  }, {
    code: 'SA',
    name: 'South Australia'
  }, {
    code: 'WA',
    name: 'Western Australia'
  }, {
    code: 'TAS',
    name: 'Tasmania'
  }, {
    code: 'NT',
    name: 'Northern Territory'
  }, {
    code: 'ACT',
    name: 'Australian Capital Territory'
  }];

  // When state changes from the header on a /programs/* route, the URL is
  // authoritative for which state the Programs page shows - so navigate it.
  const handleStateChange = (newState: string) => {
    setSelectedState(newState);
    if (location.pathname === '/programs' || location.pathname.startsWith('/programs/')) {
      const supported = ['NSW', 'VIC'];
      if (supported.includes(newState)) {
        navigate(`/programs/${newState.toLowerCase()}`);
      }
    }
  };


  const courses = coursesForState(selectedState.toUpperCase() as AustralianState)
    .filter((course) => course.homepageCategory)
    .map((course) => ({ name: course.name, href: course.href }));


  const handleCenterSelect = (centerSlug: string) => {
    
    // Use setTimeout to defer navigation and prevent dropdown lag
    setTimeout(() => {
      if (centerSlug === 'and-many-more') {
        navigate('/franchise');
      } else if (centerSlug === 'truganina') {
        navigate('/centres/truganina');
      } else {
        const center = currentStateConfig?.centers.find(c => c.slug === centerSlug);
        if (center) {
          // Update state to match center's state (in case it's different)
          if (center.stateCode !== selectedState) {
            setSelectedState(center.stateCode);
          }
          setSelectedCenter(centerSlug);
          setSelectedCenterId(center.uuid); // Store UUID for pricing queries
          
          // Only navigate to center page if not on a program page that uses pricing
          const programPagesWithPricing = ['/programs/foundations', '/programs/oc-preparation', '/programs/selective-school', '/programs/scholarship-exam-course'];
          if (!programPagesWithPricing.includes(location.pathname)) {
            navigate(`/centres/${centerSlug}`);
          }
        }
      }
    }, 0);
  };

  const getCenterOptions = () => {
    if (!currentStateConfig || !currentStateConfig.centers.length) return [];
    const centers = currentStateConfig.centers
      .map(center => ({
        value: center.slug,
        label: center.suburb,
        disabled: false,
        comingSoon: center.comingSoon || false
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
    
    const options = [...centers];
    
    // Add state-specific coming soon centers
    if (selectedState === 'NSW') {
      // Miranda removed from dropdown
    }
    
    if (selectedState === 'VIC') {
      // All VIC centers are already included from currentStateConfig.centers
    }
    
    // Add "and many more" option for all states
    options.push({
      value: 'and-many-more',
      label: 'and many more...',
      disabled: false,
      comingSoon: false
    });
    
    return options;
  };

  const getSelectedCenterLabel = () => {
    if (!selectedCenter || !currentStateConfig) return 'Find a Centre';
    const center = currentStateConfig.centers.find(c => c.slug === selectedCenter);
    return center ? center.suburb : 'Find a Centre';
  };

  const selectedCenterData = currentStateConfig?.centers.find(c => c.slug === selectedCenter);
  const isBookingDisabled = ['QLD', 'SA', 'NT', 'ACT'].includes(selectedState) || selectedCenterData?.comingSoon === true;

  return (
    <>
      <header className="bg-gradient-to-r from-orange-50/80 via-amber-50/90 to-sky-50/80 backdrop-blur-sm sticky top-0 z-[100] border-b-2 border-b-orange-400/50 shadow-sm pt-2 overflow-visible">
        <div className="container flex items-center justify-between py-4 relative">
          <Link to="/" className="flex items-center space-x-2 font-semibold">
            <div className="relative">
              {/* Glowing bubbles */}
              <div className="absolute -right-2 -top-1 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 animate-pulse opacity-70"></div>
              <div className="absolute -right-1 top-4 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-orange-300 to-amber-400 animate-pulse opacity-60" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute -left-1 bottom-0 w-1 h-1 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 animate-pulse opacity-50" style={{ animationDelay: '1s' }}></div>
              <img 
                src="/lovable-uploads/cabe315d-30f2-4193-a5cf-d75e4a5b7d1d.png" 
                alt="Schoolola Tutoring" 
                className="h-8 w-auto object-contain drop-shadow-sm relative z-10"
                width={62}
                height={32}
                style={{ aspectRatio: '62/32' }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            <button
              type="button"
              data-megamenu-trigger
              aria-haspopup="menu"
              aria-expanded={megaPanel === 'courses'}
              onClick={() => toggleMegaPanel('courses')}
              className={`text-gray-700 hover:text-primary transition-colors duration-150 flex items-center font-medium px-3 py-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                megaPanel === 'courses' ? 'bg-[#FFF9F4] text-primary' : ''
              }`}
            >
              Courses
              <ChevronDown
                className={`ml-1 h-4 w-4 transition-transform duration-150 ${megaPanel === 'courses' ? 'rotate-180' : ''}`}
              />
            </button>

            <Link
              to="/key-differentiators"
              className="text-gray-700 hover:text-primary transition-colors duration-150 font-medium hover:bg-[#FFF9F4] px-3 py-2 rounded-lg"
            >
              Key Differentiators
            </Link>

            <Link
              to="/values"
              className="text-gray-700 hover:text-primary transition-colors duration-150 font-medium hover:bg-[#FFF9F4] px-3 py-2 rounded-lg"
            >
              Our Values
            </Link>
          </nav>


          <div className="hidden md:flex items-center space-x-3">
            {/* Combined state + centre context chip - opens the Find a Centre megamenu */}
            {(() => {
              const activeCentre = currentStateConfig?.centers.find(c => c.slug === selectedCenter);
              const stateLabel = currentStateConfig?.name ?? selectedState;
              return (
                <button
                  type="button"
                  data-megamenu-trigger
                  aria-haspopup="menu"
                  aria-expanded={megaPanel === 'centres'}
                  aria-label={`Find a Centre. Currently showing ${stateLabel}${activeCentre ? `, ${activeCentre.suburb}` : ''}`}
                  onClick={() => toggleMegaPanel('centres')}
                  className={`group flex items-center gap-2 rounded-full border border-border bg-card hover:bg-[#FFF9F4] hover:border-primary/40 transition-colors duration-150 pl-3 pr-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    megaPanel === 'centres' ? 'bg-[#FFF9F4] border-primary/40' : ''
                  }`}
                >
                  <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground leading-none">
                    {stateLabel}
                  </span>
                  {activeCentre ? (
                    <>
                      <span className="text-muted-foreground/60 text-sm leading-none">·</span>
                      <span className="text-sm font-semibold text-primary leading-none truncate max-w-[140px]">
                        {activeCentre.suburb}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground leading-none ml-0.5">
                      Find a Centre
                    </span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform duration-150 ${megaPanel === 'centres' ? 'rotate-180' : ''}`}
                  />
                </button>
              );
            })()}


            <Link to="/franchise">
              <Button 
                variant="outline" 
                className="bg-gradient-to-r from-accent/10 to-accent/20 hover:from-accent/20 hover:to-accent/30 text-accent border-accent/50 hover:border-accent font-medium px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Schoolola Franchising
              </Button>
            </Link>

            {isBookingDisabled ? (
              <Button 
                variant="default" 
                disabled
                className="bg-gray-400 text-white font-medium px-6 py-2 rounded-full shadow-lg cursor-not-allowed opacity-50 border-none"
              >
                Book an Assessment
              </Button>
            ) : (
              <Link to="/book-assessment">
                <Button 
                  variant="default" 
                  className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white font-medium px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-none"
                >
                  Book an Assessment
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden hover:bg-gray-100 rounded-lg p-3 min-h-[44px] min-w-[44px]">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="sm:max-w-sm bg-white/95 backdrop-blur-sm w-full">
              <SheetHeader className="text-left">
                <SheetTitle className="text-gray-800 text-lg">Menu</SheetTitle>
                <SheetDescription className="text-gray-600 text-sm">
                  Explore Schoolola and find the right course for your child.
                </SheetDescription>
              </SheetHeader>
              <div className="grid gap-2 py-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                {/* Mobile Book Assessment - Top Priority */}
                {isBookingDisabled ? (
                  <Button disabled className="w-full bg-gray-400 text-white font-medium py-4 rounded-full shadow-lg text-base min-h-[52px] cursor-not-allowed opacity-50 border-none mb-4">
                    Book Assessment
                  </Button>
                ) : (
                  <Link to="/book-assessment" className="mb-4">
                    <Button className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-white font-medium py-4 rounded-full shadow-lg text-base min-h-[52px]">
                      Book Assessment
                    </Button>
                  </Link>
                )}

                {/* Mobile Franchising Button */}
                <Link to="/franchise" className="mb-4">
                  <Button variant="outline" className="w-full bg-gradient-to-r from-accent/10 to-accent/20 hover:from-accent/20 hover:to-accent/30 text-accent border-accent/50 hover:border-accent font-medium py-4 rounded-full shadow-lg text-base min-h-[52px]">
                    Schoolola Franchising
                  </Button>
                </Link>

                
                {/* Mobile Location Selection - Moved up for visibility */}
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <div className="px-4 text-sm font-semibold text-gray-900">Location</div>
                  <Select value={selectedState} onValueChange={handleStateChange}>
                    <SelectTrigger className="w-full bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 rounded-xl min-h-[44px]">
                      <MapPin className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Select Location" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 backdrop-blur-sm border border-gray-200/50 shadow-xl rounded-xl">
                      {states.map(state => (
                        <SelectItem key={state.code} value={state.code} className="min-h-[44px] flex items-center">
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Mobile Find a Centre */}
                {currentStateConfig && currentStateConfig.centers.length > 0 && (
                  <div className="space-y-3 border-t border-gray-200 pt-4">
                    <div className="px-4 text-sm font-semibold text-gray-900">Find a Centre</div>
                    <Select value={selectedCenter || ""} onValueChange={(value) => {
                      handleCenterSelect(value);
                      setIsMenuOpen(false);
                    }}>
                      <SelectTrigger className="w-full bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200 rounded-xl min-h-[44px]">
                        <Search className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Select a Centre" />
                      </SelectTrigger>
                      <SelectContent className="bg-white/95 backdrop-blur-sm border border-gray-200/50 shadow-xl rounded-xl z-[100]">
                        {getCenterOptions().map(option => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            textValue={option.label}
                            className={`min-h-[44px] flex items-center ${
                              option.disabled 
                                ? 'text-gray-400 cursor-default' 
                                : ''
                            }`}
                            disabled={option.disabled}
                          >
                            <span className="flex items-center gap-2">
                              {option.label}
                              {option.comingSoon && (
                                <span className="inline-flex items-center rounded-full bg-accent/20 text-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
                                  Coming Soon
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                      </Select>
                  </div>
                )}

                {/* Collapsible Courses Section */}
                <div className="space-y-2 border-t border-gray-200 pt-4">
                  <button
                    onClick={() => setIsCoursesExpanded(!isCoursesExpanded)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <span>Courses</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isCoursesExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`space-y-1 overflow-hidden transition-all duration-300 ${isCoursesExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    {courses.map((course) => (
                      <Link
                        key={course.name}
                        to={course.href}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-primary/10 hover:to-accent/10 hover:text-primary transition-all duration-200 rounded-lg min-h-[44px] flex items-center ml-4"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {course.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link 
                  to="/key-differentiators" 
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-primary/10 hover:to-accent/10 hover:text-primary transition-all duration-200 rounded-lg font-medium min-h-[44px] flex items-center border-t border-gray-200 mt-4"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Key Differentiators
                </Link>

                <Link 
                  to="/values" 
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-primary/10 hover:to-accent/10 hover:text-primary transition-all duration-200 rounded-lg font-medium min-h-[44px] flex items-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Our Values
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <MegaMenu panel={megaPanel} onClose={() => setMegaPanel(null)} />
      </header>


      {/* SCOPE Video Feature Bar - Only show on SCOPE page */}
      {location.pathname === '/scope-exam' && !hideVideoSection && (
        <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-b border-gray-100 sticky top-[73px] z-40">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Video Section */}
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
                  <DialogTrigger asChild>
                    <div className="relative bg-white rounded-lg shadow-sm overflow-hidden flex-shrink-0 cursor-pointer group transition-all duration-300 hover:shadow-md">
                      <video
                        src="/Schoolola_SCOPE_Kingswood.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-20 h-12 object-cover rounded-lg"
                        preload="metadata"
                        poster="/lovable-uploads/acabd75c-c9c6-4f49-8ac5-acefa272ee88.png"
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="absolute inset-0 bg-black/10 rounded-lg group-hover:bg-black/20 transition-colors duration-300"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Play className="h-6 w-6 text-white drop-shadow-lg" fill="white" />
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl w-fit p-0 bg-black border-none">
                    <div className="relative">
                      <video
                        src="/Schoolola_SCOPE_Kingswood.mp4"
                        autoPlay
                        className="max-h-[80vh] w-auto"
                        preload="metadata"
                        onEnded={handleVideoEnd}
                        poster="/lovable-uploads/acabd75c-c9c6-4f49-8ac5-acefa272ee88.png"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>
                    <p className="text-sm font-semibold text-gray-900 truncate">SCOPE™ Live Experience</p>
                  </div>
                  <p className="text-xs text-gray-600 truncate hidden sm:block">
                    See real exam conditions and student achievements
                  </p>
                </div>
              </div>

              {/* Call to Action - Removed */}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
